<script setup lang="ts">
import { format } from 'date-fns'
import { Printer } from 'lucide-vue-next'
import HorizontalLine from 'pejer/src/components/HorizontalLine.vue'

import DataStateDisplay from 'pejer/src/components/DataState/DataStateDisplay.vue'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from 'pejer/src/components/ui/table'
import { usePejerBase } from 'pejer/src/stores/pejerData'
import { PaymentVoucherPaymentDto } from 'pejer/src/types/PaymentVoucher'
import { computed, nextTick, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import PagePrintState from '../../../components/PagePrintState.vue'
import usePejerQuery from '../../../usePejerQuery'

const props = defineProps<{
  payoutId: string
}>()

const pejer = usePejerBase()

// const { data, isLoading, error, isError } = useQuery({
//   queryKey: ['payout', props.payoutId],
//   queryFn: async () => {
//     const res = await pejer.getPaymentVoucher(props.payoutId)

//     if ('error' in res) throw res.error

//     return res.data
//   },
//   select: (data) => {
//     // sort the payments by insertTime
//     data.payments.sort(
//       (a, b) => new Date(b.insertTime).getTime() - new Date(a.insertTime).getTime()
//     )
//     return data
//   },
// })

const { data, dataState, isLoading, isError, error } = usePejerQuery(
  pejer.urls.payouts.getPaymentVoucher,
  {
    queryKey: ['payouts', props.payoutId],
    pathArgs: {
      id: props.payoutId,
    },
    select: (data) => {
      // sort the payments by insertTime
      data.payments.sort(
        (a, b) => new Date(b.insertTime).getTime() - new Date(a.insertTime).getTime()
      )
      return data
    },
  }
)

const paymentCategories = computed(() => {
  const categories: {
    [key in PaymentVoucherPaymentDto['externalObjectIdTag']]: {
      grossAmount: number
      amount: number
      fee: number
      refundAmount: number
      vippsFeeAmount: number
      payments: PaymentVoucherPaymentDto[]
    }
  } = {}

  if (!data) return []

  // Fill the categories
  for (const payment of data.value.payments) {
    const refundAmount = -payment.ledgers
      .filter((x) => x.amount < 0 && x.description.startsWith('(Refunded):'))
      .reduce((acc, ledger) => acc + ledger.amount, 0)

    if (payment.externalObjectIdTag in categories) {
      categories[payment.externalObjectIdTag].payments.push(payment)
      categories[payment.externalObjectIdTag].grossAmount += payment.grossAmount
      categories[payment.externalObjectIdTag].refundAmount += refundAmount
      categories[payment.externalObjectIdTag].amount += payment.ledgers.reduce(
        (acc, ledger) => acc + ledger.amount,
        0
      )
      categories[payment.externalObjectIdTag].fee += payment.fee
      categories[payment.externalObjectIdTag].vippsFeeAmount += payment.vippsPartOfFee
    } else {
      categories[payment.externalObjectIdTag] = {
        grossAmount: payment.grossAmount,
        amount: payment.ledgers.reduce((acc, ledger) => acc + ledger.amount, 0),
        refundAmount: refundAmount,
        payments: [payment],
        fee: payment.fee,
        vippsFeeAmount: payment.vippsFeeAmount,
      }
    }
  }

  const categoriesArray = Object.values(categories)
  // Sort the categories
  categoriesArray.sort((a, b) => b.grossAmount - a.grossAmount)

  return categoriesArray
})

const overview = computed(() => {
  const payoutOverview = {
    totalGrossAmount: 0,
    totalNetAmount: 0,
    totalFees: 0,
    totalVippsFees: 0,
    numberOfPayments: 0,
    refundAmount: 0,
  }
  if (!data) return payoutOverview

  payoutOverview.totalNetAmount -= data.value.payoutFee
  payoutOverview.totalFees += data.value.payoutFee

  for (const payment of data.value.payments) {
    const refundAmount = -payment.ledgers
      .filter((x) => x.amount < 0 && x.description.startsWith('(Refunded):'))
      .reduce((acc, ledger) => acc + ledger.amount, 0)

    const ledgerSum = payment.ledgers.reduce((acc, ledger) => acc + ledger.amount, 0)
    payoutOverview.totalGrossAmount += payment.grossAmount
    payoutOverview.totalNetAmount += ledgerSum
    payoutOverview.refundAmount += refundAmount
    payoutOverview.numberOfPayments++
    payoutOverview.totalFees += payment.fee
    payoutOverview.totalVippsFees += payment.vippsPartOfFee
  }

  return payoutOverview
})

const formatNumber = (num: number, currency: boolean = true) => {
  return (num || 0).toLocaleString('nb-NO', { minimumFractionDigits: currency ? 2 : 0 })
}

const route = useRoute()
watch(isLoading, async (isLoading) => {
  if (!isLoading) {
    if (route.query.print) {
      await nextTick()
      await nextTick()
      window.print()
    }
  }
})

const router = useRouter()
const print = () => {
  window.open(
    router.resolve({
      name: 'payouts-payment-voucher',
      params: { id: props.payoutId.toString() },
    }).href + '?print=true&notEmbed=true',
    '_blank'
  )
}
</script>
<template>
  <PagePrintState
    :isLoading="isLoading"
    :isError="isError"
    :error="error?.error || 'En feil oppstod'"
  />

  <DataStateDisplay v-bind="dataState">
    <div class="overflow-y-auto flex flex-col gap-8 bg-white">
      <div class="flex flex-col space-y-2">
        <div>
          <h1 class="mb-2 text-xl font-semibold flex justify-between items-end">
            <span>Utbetalingsbilag</span>
            <div class="flex gap-4">
              <span>
                <span class="text-sm text-slate-700">Bilagsnummer: </span>
                <span class="font-bold text-lg">{{ data?.id }}</span>
              </span>
              <a
                href="#"
                class="no-print text-sm text-blue-500 font-medium hover:underline flex gap-2 items-center"
                @click.prevent.stop="print"
                >Skriv ut <Printer :size="16" />
              </a>
            </div>
          </h1>
          <HorizontalLine class="mb-2" />
          <span class="text-sm text-gray-600">
            Betalt til bankkontonummer <b>{{ data?.toBankAccountNumber }}</b>
            <b> ({{ data?.businessName }})</b> den
            <b>{{ data?.insertTime ? format(new Date(data.insertTime), 'dd.MM.yyyy') : '-' }}</b>
            fra kontonummer
            <b>{{ data?.fromBankAccountNumber }}</b>
          </span>
        </div>
        <Table>
          <TableBody>
            <TableRow>
              <TableCell class="py-4 font-medium whitespace-nowrap"> Antall betalinger </TableCell>
              <TableCell class="py-4 w-full">{{
                formatNumber(overview.numberOfPayments, false)
              }}</TableCell>
            </TableRow>
            <!-- <TableRow>
              <TableCell class="py-4 font-medium whitespace-nowrap">
                Totale salgsinntekter
              </TableCell>
              <TableCell class="py-4 w-full"
                >{{ formatNumber(overview.totalGrossAmount / 100) }}
                <span class="text-gray-500">NOK</span></TableCell
              >
            </TableRow> -->
            <TableRow>
              <TableCell class="py-4 font-medium whitespace-nowrap"> Utbetalt </TableCell>
              <TableCell class="py-4 w-full"
                >{{ formatNumber(data.amount / 100) }}
                <span class="text-gray-500">NOK</span></TableCell
              >
            </TableRow>
            <TableRow>
              <TableCell class="py-4 font-medium whitespace-nowrap"> Gebyrer </TableCell>
              <TableCell class="py-4 w-full"
                >{{ formatNumber(overview.totalFees / 100) }}
                <span class="text-gray-500">NOK</span>
                <br />
                <span class="text-gray-500 italic text-xs">
                  (hvorav {{ formatNumber(data.payoutFee / 100) }},- er utbetalingsgebyr)
                </span>
                <br />
                <span class="text-gray-500 italic text-xs" v-if="overview.totalVippsFees > 0">
                  (hvorav {{ formatNumber(data.vippsFee / 100) }},- er vipps-gebyr)
                </span>
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell class="py-4 font-medium whitespace-nowrap"> Refunderinger </TableCell>
              <TableCell class="py-4 w-full"
                >{{ formatNumber(overview.refundAmount / 100) }}
                <span class="text-gray-500">NOK</span></TableCell
              >
            </TableRow>
          </TableBody>
        </Table>
      </div>
      <div class="flex flex-col space-y-4">
        <div class="sticky -top-[1px] bg-white">
          <h3 class="text-lg font-semibold mb-2">Oppsummering av betalingskategoriene</h3>
          <HorizontalLine />
        </div>
        <div
          class="flex flex-col space-y-8 rounded-sm overflow-hidden border border-gray-200 break-inside-avoid"
          v-for="(category, index) in paymentCategories"
          :key="index"
        >
          <Table class="break-inside-avoid">
            <TableHeader>
              <TableRow class="border-b-primaryBlue bg-slate-100">
                <TableHead colspan="2" class="text-slate-800">
                  {{ category.payments[0].description || 'Ukjent kategori' }}
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              <TableRow>
                <TableCell class="py-4 font-medium whitespace-nowrap">
                  Antall betalinger
                </TableCell>
                <TableCell class="py-4 w-full">{{
                  formatNumber(category.payments.length, false)
                }}</TableCell>
              </TableRow>
              <!-- <TableRow>
                <TableCell class="py-4 font-medium whitespace-nowrap"> Innbetalt beløp </TableCell>
                <TableCell class="py-4 w-full"
                  >{{ formatNumber(category.grossAmount / 100) }}
                  <span class="text-gray-500">NOK</span></TableCell
                >
              </TableRow> -->
              <TableRow>
                <TableCell class="py-4 font-medium whitespace-nowrap"> Inntekter </TableCell>
                <TableCell class="py-4 w-full"
                  >{{ formatNumber(category.amount / 100) }}
                  <span class="text-gray-500">NOK</span></TableCell
                >
              </TableRow>
            </TableBody>
            <TableRow>
              <TableCell class="py-4 font-medium whitespace-nowrap"> Gebyrer </TableCell>
              <TableCell class="py-4 w-full">
                {{ formatNumber(category.fee / 100) }}
                <span class="text-gray-500">NOK</span>
                <span class="text-gray-500 italic text-xs" v-if="category.vippsFeeAmount > 0">
                  (hvorav {{ formatNumber(category.vippsFeeAmount / 100) }},- er Vipps-gebyr)
                </span>
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell class="py-4 font-medium whitespace-nowrap"> Refunderinger </TableCell>
              <TableCell class="py-4 w-full">
                {{ formatNumber(category.refundAmount / 100) }}
                <span class="text-gray-500">NOK</span>
              </TableCell>
            </TableRow>
          </Table>
        </div>
      </div>
      <div class="flex flex-col space-y-4">
        <div class="sticky -top-[1px] bg-white">
          <h3 class="mb-2 text-lg font-semibold">Kjøp som inngår i utbetalingen</h3>
          <HorizontalLine />
        </div>
        <div class="overflow-hidden rounded-md">
          <Table>
            <TableHeader class="bg-slate-100">
              <TableRow>
                <TableHead> Dato </TableHead>
                <TableHead> Varelinjer </TableHead>
                <TableHead class="whitespace-nowrap"> Innbetalt beløp </TableHead>
                <TableHead class="whitespace-nowrap"> Utbetalt beløp </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              <template v-for="(payment, index) in data.payments" :key="index">
                <TableRow>
                  <TableCell class="whitespace-nowrap">{{
                    format(new Date(payment.insertTime), 'dd.MM.yyyy')
                  }}</TableCell>
                  <TableCell class="w-full">
                    <div class="flex flex-col space-y-2">
                      <b>{{ payment.description }}</b>
                      <template v-for="(ledger, index) in payment.ledgers" :key="index">
                        <span class="text-xs"
                          >+ {{ ledger.description }} ({{ formatNumber(ledger.amount / 100) }}
                          <span class="text-gray-500">NOK</span>)</span
                        >
                      </template>
                    </div>
                  </TableCell>
                  <TableCell class="whitespace-nowrap"
                    >{{ formatNumber(payment.grossAmount / 100) }}
                    <span class="text-gray-500">NOK</span></TableCell
                  >
                  <TableCell class="whitespace-nowrap"
                    >{{
                      formatNumber(
                        payment.ledgers.reduce((acc, ledger) => acc + ledger.amount, 0) / 100
                      )
                    }}
                    <span class="text-gray-500">NOK</span></TableCell
                  >
                </TableRow>
              </template>
            </TableBody>
          </Table>
        </div>
      </div>
    </div>
  </DataStateDisplay>
</template>

<style scoped>
@media print {
  .no-print {
    display: none;
  }
}
</style>
